/*
 * Что чат помнит на устройстве попроще очереди: текст в поле ввода по чатам и
 * место, где человек остановился в ленте (решение 04.11.2026). Ключ — человек
 * и чат: на телефоне могут сменить пользователя, чужие черновики следующему
 * показываться не должны.
 *
 * localStorage может не быть или он может отказать (приватное окно, переполнен):
 * тогда память молча пуста, а поле работает как раньше.
 */

const PREFIX = 'chat.mem.';

const textKey = (userId: string, channelId: string, parentId: number | null) =>
  `${PREFIX}${userId}.text.${channelId}.${parentId ?? 'root'}`;
const posKey = (userId: string, channelId: string) => `${PREFIX}${userId}.pos.${channelId}`;

/*
 * Пометка «забыт» после выхода, как у очереди (deviceStore.clear): поле ввода
 * дописывает текст при закрытии экрана, лента запоминает место при уходе — и
 * то и другое случается уже после стирания и вернуло бы стёртое. Пометка
 * лежит в том же localStorage, а не в памяти вкладки: соседние вкладки
 * выходят вместе с этой и закрывают свои экраны позже. Снимает её первое
 * чтение этого человека — оно бывает только после нового входа.
 */
const forgottenKey = (userId: string) => `${PREFIX}forgotten.${userId}`;

const isForgotten = (userId: string) => localStorage.getItem(forgottenKey(userId)) !== null;

/** Чтение после входа: человек снова здесь, запись разрешена. */
const remember = (userId: string) => localStorage.removeItem(forgottenKey(userId));

export function loadText(userId: string, channelId: string, parentId: number | null): string {
  try {
    remember(userId);
    return localStorage.getItem(textKey(userId, channelId, parentId)) ?? '';
  } catch {
    return '';
  }
}

/** Пустой текст запись удаляет: пустое поле не должно оставлять хлам. */
export function saveText(userId: string, channelId: string, parentId: number | null, text: string): void {
  try {
    if (isForgotten(userId)) return;
    const key = textKey(userId, channelId, parentId);
    if (text) localStorage.setItem(key, text);
    else localStorage.removeItem(key);
  } catch {
    /* память недоступна — не страшно */
  }
}

/** Где человек остановился в ленте: верхнее видимое сообщение и его сдвиг от верха окна. */
export type ReadPosition = { id: number; offset: number };

export function loadPosition(userId: string, channelId: string): ReadPosition | null {
  try {
    remember(userId);
    const raw = localStorage.getItem(posKey(userId, channelId));
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<ReadPosition>;
    return typeof p.id === 'number' && typeof p.offset === 'number' ? { id: p.id, offset: p.offset } : null;
  } catch {
    return null;
  }
}

/** null — места нет (лента в конце): запись удаляется. */
export function savePosition(userId: string, channelId: string, pos: ReadPosition | null): void {
  try {
    if (isForgotten(userId)) return;
    if (pos) localStorage.setItem(posKey(userId, channelId), JSON.stringify(pos));
    else localStorage.removeItem(posKey(userId, channelId));
  } catch {
    /* память недоступна — откроется в конце, как раньше */
  }
}

/** Выход из учётной записи: стереть всё, что устройство помнило про человека, и пометить его забытым. */
export function forgetAll(userId: string): void {
  try {
    localStorage.setItem(forgottenKey(userId), '1');
    const own = `${PREFIX}${userId}.`;
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(own)) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* см. выше */
  }
}
